import React, { createContext, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../services/auth.service';
import { AuthDto } from '../services/dtos/auth.dto';
import { useLocalStorage } from './use-local-storage.hook';
import { getUser, getUserToken } from '../services/account.service';
import jwtDecode from 'jwt-decode';
import { ConnectedUserDto } from '../services/dtos/connected-user-dto';
import { useSnackbars } from './snackbar.hook';
import authVerify from './auth-verify.hook';
import { showAxiosMessageError } from '../services/error-message.service';

export interface AuthContextType {
    user: ConnectedUserDto | null;
    signin: (data: AuthDto) => Promise<void>;
    signout: () => void;
    loggedInAs: (id: string, name: string, firstName: string) => void;
    signoutLoggedInAs: () => void;
    expired: () => void;
    loginWithToken: (token: string) => void;
}

export const AuthContext = createContext<AuthContextType>(null!);

const timeToWait = 100;
/**
 * Hook gérant la connexion et déconnexion
 * @param children Element React à afficher
 * @param userData Informations de l'utilisateur
 * @constructor
 */
export const AuthProvider = ({ children, userData }: { children: React.ReactNode; userData: any }) => {
    //State plus objects necessairesua provider
    let [user, setUser] = useLocalStorage('user', userData);
    const navigate = useNavigate();
    const showMessage = useSnackbars();

    const value = useMemo(() => {
        const signin = async (data: AuthDto) => {
            // Récupération du token, enregistrement de la connexion et retour à la page d'accueil
            await login(data)
                .then(async token => {
                    if (token) {
                        //On décode le token pour le récupérer sous forme d'objet
                        let userConnected: ConnectedUserDto = await jwtDecode(token);
                        userConnected.token = token;
                        await setUser(userConnected);
                        let dataUser = await getUser(userConnected.userId);
                        userConnected.nom = dataUser?.nomUsage;
                        userConnected.prenom = dataUser?.prenom;
                        await setUser(userConnected);
                    }
                    navigate('/', { replace: true });
                })
                .catch(() => {
                    //Affichage d'erreur
                    showMessage.errorAlert('Votre login et/ou mot de passe est invalide');
                });
        };
        const signout = async () => {
            // Suppression de la connexion et retour à la page d'accueil
            const sleepForRedirect = (ms: number | undefined) =>
                new Promise(r => {
                    setUser(null);
                    setTimeout(r, ms);
                });
            await sleepForRedirect(timeToWait);
            showMessage.successAlert('Déconnexion réussie');
            localStorage.clear();
            navigate('/', { replace: true });
        };
        const loggedInAs = async (id: string, name: string, firstName: string) => {
            await getUserToken(id)
                .then(async result => {
                    if (result) {
                        user.tokenLoggedInAS = result;
                        user.nomLoggedInAS = name;
                        user.prenomLoggedInAS = firstName;
                        user.idLoggedAs = id;
                        await setUser(user);
                        showMessage.successAlert('Connexion réussie');
                        navigate('/', { replace: true });
                    }
                })
                .catch(error => {
                    console.error('Erreur lors de la récupération du contrat');
                    showAxiosMessageError(error, showMessage);
                });
        };
        const signoutLoggedInAs = async () => {
            user.tokenLoggedInAS = null;
            user.nomLoggedInAS = null;
            user.prenomLoggedInAS = null;
            user.idLoggedAs = null;
            await setUser(user);
            showMessage.successAlert('Déconnexion réussie');
            navigate('/', { replace: true });
        };
        const expired = async () => {
            // Suppression de la connexion et retour à la page de connexion
            // Avec un message informant la personne
            await setUser(null);
            navigate('/connexion');
            showMessage?.infoAlert('Votre session a expiré. Merci de vous reconnecter.');
        };

        const loginWithToken = async (token: string) => {
            // Récupération du token, enregistrement de la connexion et retour à la page d'accueil
            let userConnected: ConnectedUserDto = await jwtDecode(token);
            userConnected.token = token;
            await setUser(userConnected);
            let dataUser = await getUser(userConnected.userId);
            userConnected.nom = dataUser?.nomUsage;
            userConnected.prenom = dataUser?.prenom;
            await setUser(userConnected);
        };

        return { user, signin, signout, loggedInAs, signoutLoggedInAs, expired, loginWithToken };
    }, [user, setUser, navigate, showMessage]);

    // Vérifie le token
    authVerify(value);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
